import React, { useEffect, useRef, useState } from "react";
import { Select } from "@mantine/core";
import { useServerApi } from "../../hooks/userServerApi";

const sortPeriodsByName = (periods = []) =>
  [...periods].sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), undefined, {
      numeric: true,
    })
  );

const mergePeriods = (docs = [], currentPeriod) => {
  const byId = new Map();
  docs.forEach((period) => byId.set(period._id, period));
  if (currentPeriod?._id) {
    byId.set(currentPeriod._id, currentPeriod);
  }
  return sortPeriodsByName([...byId.values()]);
};

const FiscalPeriodSelect = ({
  value,
  onChange,
  size = "sm",
  label = "Fiscal Period",
  clearable = true,
  style,
}) => {
  const [api] = useServerApi();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const autoSelectedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [currentResult, pageResult] = await Promise.allSettled([
          api.Accounting.getCurrentFiscalPeriod(),
          api.search({
            apiEntity: "fiscalPeriod",
            pageSize: 100,
            currentPage: 1,
            sort: "name",
            order: "asc",
          }),
        ]);

        if (cancelled) return;

        const current =
          currentResult.status === "fulfilled" ? currentResult.value : null;
        const page =
          pageResult.status === "fulfilled" ? pageResult.value : null;
        const sorted = mergePeriods(page?.docs || [], current?.period);

        setPeriods(sorted);

        const currentId = current?.period?._id;
        let selectedId = null;
        if (currentId && sorted.some((p) => p._id === currentId)) {
          selectedId = currentId;
        } else if (sorted.length > 0) {
          const openPeriod = sorted.find((p) => p.status === "OPEN");
          selectedId = openPeriod?._id || sorted[sorted.length - 1]._id;
        }

        if (!valueRef.current && !autoSelectedRef.current && selectedId) {
          autoSelectedRef.current = true;
          onChangeRef.current?.(selectedId);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("FiscalPeriodSelect load failed", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = periods.map((period) => ({
    value: period._id,
    label: `${period.name || period.code || period._id} (${period.status || "OPEN"})`,
  }));

  return (
    <Select
      size={size}
      label={label}
      placeholder="Select fiscal period"
      data={data}
      value={value || null}
      onChange={onChange}
      clearable={clearable}
      searchable
      disabled={loading}
      style={style}
    />
  );
};

export default FiscalPeriodSelect;
